import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Autocomplete,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";

import DatePickerV1 from "components/DatePickerV1";
import ButtonV1 from "components/ButtonV1";
import useToaster from "hooks/useToaster";
import { searchVendors } from "services/availabilityService";
import {
  dedupeVendorSelectOptions,
  jodlGstMatchesSellerSearchQuery,
  normalizeVendorLocationSelection,
  prependSelectAllLocationOption,
  realKeysFromLocationOptions,
  SELECT_ALL_LOCATIONS_VALUE,
  stripSelectAllToken,
  vendorLocationAutocompleteValue,
} from "utils/locationFilterUtils";
import { AVAILABILITY_FILTER_FIELDS } from "constants/availabilityConfig";

const AvailabilityFilters = ({
  filters,
  errors = {},
  onFilterChange,
  onFilterBlur,
  onApplyFilters,
  onResetFilters,
}) => {
  const QTY_TYPE_OPTIONS = [
    { label: "Planned", value: "planned" },
    { label: "Derived inventory", value: "derived_inventory" },
    { label: "Physical Inventory", value: "physical_inventory" },
    { label: "Net Available", value: "net_available" },
    { label: "Reserved", value: "reserved" },
  ];
  const triggerToaster = useToaster();
  const [sellerOptions, setSellerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [allSellersData, setAllSellersData] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const debounceTimerRef = useRef(null);
  const prevVendorIdsRef = useRef([]);

  // ─── Seller search ───────────────────────────────────────────────────────────

  const fetchSellers = useCallback(async (query) => {
    if (query.length < 3) return;
    setLoadingSellers(true);
    try {
      const panList = await searchVendors(query);
      const options = dedupeVendorSelectOptions(
        panList.flatMap((pan) =>
          (pan.gst || [])
            .filter(
              (gst) =>
                gst.legal_entity === "JODL" &&
                jodlGstMatchesSellerSearchQuery(gst, query),
            )
            .map((gst) => ({
              label: gst.display_name ?? pan.pan_name ?? "-",
              value: gst.vendor_key ?? "-",
              extraInfo: `GST: ${gst.gst_number} | ${gst.legal_entity}`,
            })),
        ),
      );
      setSellerOptions(options);
      setAllSellersData(panList);
    } catch {
      triggerToaster("Failed to fetch sellers", "error");
      setSellerOptions([]);
    } finally {
      setLoadingSellers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSellerInputChange = useCallback(
    (_, newInputValue, reason) => {
      if (reason !== "input") return;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (newInputValue.length >= 3) {
        debounceTimerRef.current = setTimeout(
          () => fetchSellers(newInputValue),
          500,
        );
      }
    },
    [fetchSellers],
  );

  // ─── Location population on seller change ────────────────────────────────────

  useEffect(() => {
    prevVendorIdsRef.current = [];
    if (filters?.seller) {
      const selectedGst = allSellersData
        .flatMap((pan) => pan.gst || [])
        .find((gst) => gst.vendor_key === filters.seller);

      if (selectedGst?.locations?.length) {
        const locations = selectedGst.locations.map((loc) => {
          const place = [loc.city, loc.state].filter(Boolean).join(", ") || "-";
          const displayName =
            (loc.location_display_name &&
              String(loc.location_display_name).trim()) ||
            place;
          const vlk = loc.vendor_location_key ?? "-";
          return {
            label: displayName,
            value: vlk,
            extraInfo: `${place} | ${vlk}`,
          };
        });
        const withSelectAll = prependSelectAllLocationOption(locations);
        setLocationOptions(withSelectAll);
        const allKeys = locations.map((l) => l.value);
        prevVendorIdsRef.current = allKeys;
        onFilterChange({ name: "vendor_ids" }, allKeys);
      } else {
        setLocationOptions([]);
        onFilterChange({ name: "vendor_ids" }, []);
      }
    } else {
      setLocationOptions([]);
      onFilterChange({ name: "vendor_ids" }, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.seller]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleVendorIdsChange = (_, newValue) => {
    const rawValues = newValue.map((opt) =>
      typeof opt === "string" ? opt : opt.value,
    );
    const allRealKeys = realKeysFromLocationOptions(locationOptions);
    const next = normalizeVendorLocationSelection(
      rawValues,
      allRealKeys,
      prevVendorIdsRef.current,
    );
    prevVendorIdsRef.current = next;
    onFilterChange({ name: "vendor_ids" }, next);
  };

  const handleDateChange = (name, val, defaultTime) => {
    if (val && defaultTime) {
      const datePart = moment(val).format("YYYY-MM-DD");
      onFilterChange({ name }, `${datePart}T${defaultTime}`);
    } else {
      onFilterChange({ name }, val || null);
    }
  };

  const handleApplyFilters = () => {
    if (filters?.to_date && moment(filters.to_date).isBefore(moment(), "day")) {
      triggerToaster("Date cannot be in the past", "error");
      return;
    }

    const mmDescription = filters?.mm_description?.trim();
    if (mmDescription && mmDescription.length > 0 && mmDescription.length < 3) {
      triggerToaster("Please enter 3 characters to perform search", "error");
      return;
    }

    const payload = AVAILABILITY_FILTER_FIELDS.reduce((collection, field) => {
      let value = filters[field.name];

      if (field.name === "vendor_ids") {
        if (Array.isArray(value))
          value = stripSelectAllToken(value.map(String));
      }

      if (!value && value !== 0) return collection;
      if (Array.isArray(value) && value.length === 0) return collection;

      if (field.name === "mm_ids") {
        if (typeof value === "string") {
          collection[field.name] = value.split(",").map(v => v.trim()).filter(Boolean);
        } else {
          collection[field.name] = Array.isArray(value) ? value : [value];
        }
      } else if (["vendor_ids", "seller"].includes(field.name)) {
        collection[field.name] = Array.isArray(value) ? value : [value];
      } else {
        collection[field.name] = value;
      }
      return collection;
    }, {});

    onApplyFilters({ ...payload, page: 0, size: 20 });
  };

  // ─── Location autocomplete value ─────────────────────────────────────────────

  const allRealLocationKeys = realKeysFromLocationOptions(locationOptions);
  const locationAutocompleteValue = vendorLocationAutocompleteValue(
    filters?.vendor_ids,
    allRealLocationKeys,
  );
  const locationValueObjects = locationAutocompleteValue
    .map((v) => locationOptions.find((o) => o.value === v))
    .filter(Boolean);

  const selectedSellerOption =
    sellerOptions.find((o) => o.value === filters?.seller) ?? null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: grey[300],
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {/* MM IDs */}
        <Grid item xs={12} sm={2}>
          <TextField
            label="Search MMid"
            size="small"
            fullWidth
            value={filters?.mm_ids ?? ""}
            onChange={(e) => onFilterChange({ name: "mm_ids" }, e.target.value)}
            onBlur={() => onFilterBlur?.({ name: "mm_ids" }, filters?.mm_ids)}
            error={!!errors?.mm_ids}
            helperText={errors?.mm_ids || ""}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12} sm={2}>
          <TextField
            label="Search Description"
            size="small"
            fullWidth
            value={filters?.mm_description ?? ""}
            onChange={(e) => onFilterChange({ name: "mm_description" }, e.target.value)}
            onBlur={() => onFilterBlur?.({ name: "mm_description" }, filters?.mm_description)}
            error={!!errors?.mm_description}
            helperText={errors?.mm_description || ""}
          />
        </Grid>

        {/* VMID */}
        <Grid item xs={12} sm={2}>
          <TextField
            label="Search VMID"
            size="small"
            fullWidth
            value={filters?.vmid ?? ""}
            onChange={(e) => onFilterChange({ name: "vmid" }, e.target.value)}
          />
        </Grid>

        {/* Seller (searchable autocomplete) */}
        <Grid item xs={12} sm={3}>
          <Autocomplete
            size="small"
            options={sellerOptions}
            value={selectedSellerOption}
            getOptionLabel={(opt) => opt?.label ?? ""}
            onInputChange={handleSellerInputChange}
            onChange={(_, newValue) =>
              onFilterChange({ name: "seller" }, newValue?.value ?? null)
            }
            loading={loadingSellers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="All Sellers"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSellers && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={(x) => x}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            noOptionsText={
              loadingSellers ? "Searching..." : "Type 3+ chars to search"
            }
          />
        </Grid>

        {/* Locations (multi-select, depends on seller) */}
        <Grid item xs={12} sm={3}>
          <Autocomplete
            multiple
            size="small"
            options={locationOptions}
            value={locationValueObjects}
            getOptionLabel={(opt) => opt?.label ?? ""}
            onChange={handleVendorIdsChange}
            disabled={!filters?.seller}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
              const isSelectAll = option.value === SELECT_ALL_LOCATIONS_VALUE;
              const allSelected = allRealLocationKeys.every((k) =>
                (filters?.vendor_ids || []).includes(k),
              );
              return (
                <li {...props}>
                  <input
                    type="checkbox"
                    checked={isSelectAll ? allSelected : selected}
                    readOnly
                    style={{ marginRight: 8 }}
                  />
                  {option.label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="All Locations" />
            )}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
          />
        </Grid>

        {/* Date */}
        <Grid item xs={12} sm={2}>
          <DatePickerV1
            label="Date"
            value={filters?.to_date ? new Date(filters.to_date) : null}
            onChange={(val) => handleDateChange("to_date", val, "23:59:59")}
            dateStyle={{ width: "100%" }}
          />
        </Grid>

        {/* QTY Type */}
        <Grid item xs={12} sm={3}>
          <Autocomplete
            size="small"
            options={QTY_TYPE_OPTIONS}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
            value={QTY_TYPE_OPTIONS.find((o) => o.value === filters?.qty_type) || null}
            onChange={(_, newValue) => onFilterChange({ name: "qty_type" }, newValue?.value ?? null)}
            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
            renderInput={(params) => <TextField {...params} label="QTY type" />}
          />
        </Grid>

        {/* Condition */}
        <Grid item xs={12} sm={3}>
          <Autocomplete
            size="small"
            disabled={!filters?.qty_type}
            options={["> zero", "No criteria", "= zero"]}
            value={filters?.qty_condition ?? null}
            onChange={(_, newValue) => onFilterChange({ name: "qty_condition" }, newValue)}
            renderInput={(params) => <TextField {...params} label={!filters?.qty_type ? "Select QTY type" : "Condition"} />}
          />
        </Grid>

        {/* Action buttons */}
        <Grid item xs={12} sm="auto" sx={{ display: "flex", gap: 1 }}>
          <ButtonV1
            title="Apply Filters"
            onClick={handleApplyFilters}
            size="small"
          />
          <ButtonV1
            title="Reset"
            onClick={onResetFilters}
            size="small"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

AvailabilityFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onFilterBlur: PropTypes.func,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
};

export default AvailabilityFilters;
